import { useEffect, useState } from 'react'

import { MountApp } from 'CommonAppsEntrypoint'
import { whenFeatureFlagsReady } from 'shared/services/featureFlags'
import { logger } from 'shared/services/logger'

import { registerRemote } from '../../../federation'
import { createFederatedModuleError, toFederatedModuleError } from '../utils'

const moduleCache = new Map<string, Module>()
const scriptCache = new Map<string, Promise<void>>()
const remoteEntryCache = new Map<string, Promise<string>>()
// Guards against two concurrent loads of the same remote+entry racing inside
// Module Federation's shared-scope registration (e.g. StrictMode's
// mount-cleanup-remount double-invoking the loading effect).
const inFlightLoads = new Map<string, Promise<Module | null>>()

function loadExternalScript(src: string): Promise<void> {
  const cached = scriptCache.get(src)
  if (cached) return cached

  if (document.querySelector(`script[src="${src}"]`)) {
    const resolved = Promise.resolve()
    scriptCache.set(src, resolved)
    return resolved
  }

  const promise = new Promise<void>(resolve => {
    const script = document.createElement('script')
    script.src = src
    script.setAttribute('fetchpriority', 'high')
    script.onload = () => resolve()
    script.onerror = () => {
      logger.error('federation.runtime_env_script_failed', { url: src })
      resolve()
    }
    document.head.appendChild(script)
  })

  scriptCache.set(src, promise)
  return promise
}

async function fetchCdnRemoteEntry(cdnUrl: string): Promise<string> {
  const configUrl = `${cdnUrl.replace(/\/+$/, '')}/config.json`
  // config.json is the freshness pointer to an immutable hash-versioned
  // remoteEntry — it has to revalidate even behind a long CDN max-age.
  const response = await fetch(configUrl, { cache: 'no-cache' })
  if (!response.ok) {
    throw new Error(
      `Failed to fetch CDN config from ${configUrl}: ` +
        `${response.status} ${response.statusText}`
    )
  }

  const config = (await response.json()) as { remoteEntry?: string }
  const remoteEntry = config?.remoteEntry
  if (!remoteEntry) {
    throw new Error(
      `CDN config at ${configUrl} does not contain a "remoteEntry" field.`
    )
  }

  // remoteEntry is the full URL to remoteEntry.js (e.g.
  // https://cdn.example.com/mfe/feedback-app/abcd/remoteEntry.js).
  // registerRemote expects the base URL and appends `/remoteEntry.js`.
  return remoteEntry.replace(/\/remoteEntry\.js$/, '')
}

function resolveCdnRemoteEntry(cdnUrl: string): Promise<string> {
  const cached = remoteEntryCache.get(cdnUrl)
  if (cached) return cached

  const promise = fetchCdnRemoteEntry(cdnUrl).catch((error: unknown) => {
    // Never cache a failure — the next mount has to be free to retry.
    remoteEntryCache.delete(cdnUrl)
    throw error
  })

  remoteEntryCache.set(cdnUrl, promise)
  return promise
}

async function resolveRemoteEntryBaseUrl(
  url: string | undefined,
  cdnUrl: string | undefined
): Promise<{ resolvedUrl: string; usedCdn: boolean }> {
  const directUrl = url?.trim()
  const cdnBaseUrl = cdnUrl?.trim()

  if (cdnBaseUrl) {
    try {
      const resolvedUrl = await resolveCdnRemoteEntry(cdnBaseUrl)
      return { resolvedUrl, usedCdn: true }
    } catch (error) {
      if (!directUrl) throw error
      logger.exception(
        'federation.cdn_lookup_failed',
        error,
        { cdnUrl: cdnBaseUrl, fallbackUrl: directUrl },
        { category: 'federation' }
      )
    }
  }

  if (!directUrl) {
    throw new Error('No remote URL configured (url and cdnUrl are both empty).')
  }

  return { resolvedUrl: directUrl, usedCdn: false }
}

async function loadFederatedModule(
  url: string | undefined,
  scope: string,
  entry: string,
  envKey: string,
  cdnUrl: string | undefined
) {
  // Keyed on scope+entry, not on the URL: for CDN remotes the URL is only
  // resolved after the config.json hop, so a URL-based key never matches
  // what the caller can compute up front.
  const cacheKey = `${scope}-${entry}`
  const cached = moduleCache.get(cacheKey)
  if (cached) return cached

  const inFlight = inFlightLoads.get(cacheKey)
  if (inFlight) return inFlight

  const loadPromise = (async () => {
    // Remotes read feature flags synchronously, so the first load waits for
    // them. Runs in parallel with the remote entry lookup and never rejects.
    const [{ resolvedUrl, usedCdn }] = await Promise.all([
      resolveRemoteEntryBaseUrl(url, cdnUrl),
      whenFeatureFlagsReady()
    ])

    const runtimeEnvScript = window.__MFE_ENV_SCRIPTS__?.[envKey]
    if (runtimeEnvScript) {
      await loadExternalScript(
        runtimeEnvScript.replace(
          '{{ ENV_NAME }}',
          process.env.REACT_APP_ENVIRONMENT ?? ''
        )
      )
    }

    const remoteEntryPath = `${scope}${entry.replace('./', '/')}`
    const loadFrom = (baseUrl: string) =>
      registerRemote(baseUrl, scope).loadRemote<Module>(remoteEntryPath)

    const directUrl = url?.trim()

    let module: Module | null = null
    try {
      module = await loadFrom(resolvedUrl)
    } catch (error) {
      // The remoteEntry.js fetch itself (not just config.json) can fail even
      // after a successful CDN lookup — e.g. the CDN is briefly unreachable or
      // the file 404s. Fall back to the direct path silently, same as a
      // config.json failure, instead of surfacing an error to the user.
      if (usedCdn && directUrl && directUrl !== resolvedUrl) {
        logger.exception(
          'federation.cdn_remote_load_failed',
          error,
          { resolvedUrl, fallbackUrl: directUrl, scope, entry },
          { category: 'federation' }
        )
        module = await loadFrom(directUrl)
      } else {
        throw error
      }
    }

    if (module) {
      moduleCache.set(cacheKey, module)
    }

    return module
  })()

  inFlightLoads.set(cacheKey, loadPromise)
  try {
    return await loadPromise
  } finally {
    inFlightLoads.delete(cacheKey)
  }
}

export type Module = {
  mountApp: MountApp
  error?: string
}

export type FederatedComponentMeta = {
  url?: string
  // When set, the remoteEntry URL is looked up from `${cdnUrl}/config.json`
  // instead of being derived from `url`. `url` stays as the fallback.
  cdnUrl?: string
  scope: string
  entryPath: string
  envKey: string
}

export const useFederatedComponent = (
  remoteUrl: FederatedComponentMeta['url'],
  scope: FederatedComponentMeta['scope'],
  moduleEntryPoint: FederatedComponentMeta['entryPath'],
  envKey: FederatedComponentMeta['envKey'],
  disabled?: boolean,
  cdnUrl?: FederatedComponentMeta['cdnUrl']
) => {
  const key = `${scope}-${moduleEntryPoint}`
  const cached = moduleCache.get(key)
  const [module, setModule] = useState<Module | null>(cached ?? null)
  const [isLoaded, setIsLoaded] = useState(!!cached)
  const [isError, setIsError] = useState(false)
  const [missingRemoteUrl, setMissingRemoteUrl] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    const cachedModule = moduleCache.get(key)
    setModule(cachedModule ?? null)
    setIsLoaded(!!cachedModule)
    setIsError(false)
    setMissingRemoteUrl(false)
    setError(null)
  }, [key])

  useEffect(() => {
    if (disabled) {
      setIsLoaded(false)
      setIsError(false)
      setMissingRemoteUrl(false)
      setError(null)
      return
    }

    if (!remoteUrl?.trim() && !cdnUrl?.trim()) {
      setMissingRemoteUrl(true)
      setIsLoaded(true)
      setIsError(true)
      const err = createFederatedModuleError(
        `Remote URL is not configured for scope "${scope}" ` +
          `and entry "${moduleEntryPoint}", ` +
          `current provided remote URL is "${remoteUrl}".`,
        { remoteUrl, scope, entry: moduleEntryPoint }
      )
      setError(err)
      logger.exception(
        'federation.remote_configuration_missing',
        err,
        { scope, entry: moduleEntryPoint },
        { category: 'federation' }
      )
      return
    }

    setMissingRemoteUrl(false)

    if (module) {
      setIsLoaded(true)
      setIsError(false)
      setError(null)
      return
    }

    let cancelled = false
    const loadStartedAt = performance.now()

    loadFederatedModule(remoteUrl, scope, moduleEntryPoint, envKey, cdnUrl)
      .then(module => {
        if (cancelled) return
        setModule(module)
        setIsLoaded(true)
        setIsError(false)
        setError(null)
        logger.track('federation.module_load_completed', {
          scope,
          entry: moduleEntryPoint,
          durationMs: Math.round(performance.now() - loadStartedAt)
        })
      })
      .catch(e => {
        if (cancelled) return
        const err = toFederatedModuleError(e, {
          remoteUrl,
          scope,
          entry: moduleEntryPoint
        })
        setIsLoaded(true)
        setIsError(true)
        setError(err)
        logger.exception(
          'federation.module_load_failed',
          err,
          {
            scope,
            entry: moduleEntryPoint,
            remoteUrl,
            cdnUrl,
            durationMs: Math.round(performance.now() - loadStartedAt)
          },
          { category: 'federation' }
        )
      })

    return () => {
      cancelled = true
    }
  }, [
    disabled,
    module,
    key,
    scope,
    remoteUrl,
    moduleEntryPoint,
    envKey,
    cdnUrl
  ])

  if (error) throw error

  return { module, isLoaded, isError, missingRemoteUrl }
}
